<template>
  <main class="app-layout">
    <ContainerLoader v-if="isLoading"></ContainerLoader>
    <section
      v-if="!isLoading"
      id="cabinet"
      class="cabinet-wrapp cabinet-background app-layout__cabinet"
    >
      <SidebarMain
        v-if="navbarType === 'main'"
      ></SidebarMain>
      <sidebar v-if="navbarType === 'profile'"></sidebar>
      <div class="cabinet_main app-layout__main">
        <navbar
          :layout="navbarType"
        ></navbar>
        <Breadcrumbs />
        <FrontendUpdateNotice />

        <div class="app-layout__content">
          <router-view></router-view>
        </div>
        <RegulationFooterLink class="app-layout__footer" />
      </div>
    </section>
  </main>
</template>

<script>
import Navbar from "../components/Navbar";
import Breadcrumbs from "~/components/Common/Breadcrumbs.vue";
import Sidebar from "../components/Sidebar";
import SidebarMain from "../components/SidebarMain";
import { csrf } from "~/api/auth";
import baseMixin from "~/mixins/base";
import { reloadData } from "~/constants/broadcast/reloadDataType";
import router from "~/router";
import contractBaseMixin from "~/mixins/contractBase";
import { show } from "~/api/contract";
import FrontendUpdateNotice from "~/components/System/FrontendUpdateNotice.vue";
import RegulationFooterLink from "~/components/Common/RegulationFooterLink.vue";

const FRONTEND_UPDATE_PUBLIC_CHANNEL = process.env.VUE_APP_SYSTEM_CHANNEL;

export default {
  props: {
    navbarType: {
      type: String,
      default: "profile"
    }
  },
  mixins: [baseMixin, contractBaseMixin],
  data() {
    return {
      isAuthUser: false,
      isUserChecked: false,
      echoChannel: null,
      isMobile: window.innerWidth < 992
    };
  },
  components: {
    Navbar,
    Sidebar,
    SidebarMain,
    Breadcrumbs,
    FrontendUpdateNotice,
    RegulationFooterLink
  },
  async created() {
    this.startLoading();
    await csrf();
    let user = await this.$store.dispatch("user/fetchUser");

    if (user) {
      await Promise.all([
        this.$store.dispatch("counterparty/fetchCounterparties"),
        this.$store.dispatch("contract/fetchContracts"),
        this.$store.dispatch("contract/fetchActiveContract"),
        this.$store.dispatch("user/fetchBroadcast"),
        this.$store.dispatch("contract/fetchJoinedContracts"),
      ]);

      const contractName = this.$route.params?.contractName;
      
      if (contractName) {
        const hasAccess = await this.checkContractAccess(contractName);
        if (!hasAccess) {
          return;
        }
      }

      window.Echo.private(`user.${user.id}`).notification((notification) => {
        this.handleNotification(notification);
      });

      this.subscribeToFrontendUpdateChannel();
      this.subscribeToContractChannel();
    }

    this.finishLoading();
    window.addEventListener("resize", this.handleResize);
    this.handleResize();
  },
  beforeDestroy() {
    this.unsubscribeFromChannels();
    if (FRONTEND_UPDATE_PUBLIC_CHANNEL) {
      window.Echo.leave(FRONTEND_UPDATE_PUBLIC_CHANNEL);
    }
    window.removeEventListener("resize", this.handleResize);
  },
  watch: {
    activeContractName(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.subscribeToContractChannel();
      }
    },
    async '$route.params.contractName'(contractName, fromContractName) {
      if (!contractName || contractName === fromContractName) return;

      const contracts = this.$store.getters["contract/list"];
      if (!contracts || contracts.length === 0) return;

      await this.checkContractAccess(contractName);
    },
  },
  methods: {
    subscribeToFrontendUpdateChannel() {
      if (!FRONTEND_UPDATE_PUBLIC_CHANNEL) {
        throw new Error("VUE_APP_SYSTEM_CHANNEL is required");
      }

      window.Echo.channel(FRONTEND_UPDATE_PUBLIC_CHANNEL).listenToAll(
        (_eventName, payload) => {
          this.handleNotification(payload);
        }
      );
    },
    async checkContractAccess(contractName) {
      try {
        const contracts = this.$store.getters["contract/list"];
        const foundContract = contracts?.find(contract => contract.name === contractName);

        if (!foundContract) {
          this.$router.replace({ name: "error.404" });
          return false;
        }

        await show(foundContract.id);
        return true;
      } catch (error) {
        this.$router.replace({ name: "error.404" });
        return false;
      }
    },
    handleNotification(notification) {
      if (!notification || notification.type !== "reload-data") {
        return;
      }

      const currentRouteName = router.currentRoute.value.name;
      const modifiedData = Number(notification.modifiedData);

      if (!Number.isFinite(modifiedData)) {
        return;
      }

      const dataToReload = reloadData[modifiedData];
      if (!dataToReload) {
        return;
      }

      dataToReload.alwaysFetch?.forEach((fetchAction) => {
        this.$store.dispatch(fetchAction);
      });

      dataToReload.conditionalFetch.forEach((condition) => {
        if (condition.routeName.includes(currentRouteName)) {
          condition.fetchData.forEach((fetchAction) => {
            if (typeof fetchAction === "object" && fetchAction.action) {
              const params =
                fetchAction.paramsKey && notification.data
                  ? {
                      [fetchAction.paramsKey]:
                        notification.data[fetchAction.paramsKey]
                    }
                  : undefined;
              this.$store.dispatch(fetchAction.action, params);
            } else {
              if (fetchAction === "contractCustomerStorage/fetchCustomers") {
                this.$store.dispatch(fetchAction, {});
              } else {
                const contractId = this.activeContract?.id;
                this.$store.dispatch(fetchAction, contractId);
              }
            }
          });
        }
      });
    },
    subscribeToContractChannel() {
      this.unsubscribeFromChannels();
      if (this.activeContractName) {
        this.echoChannel = window.Echo.private(
          `contract.${this.activeContractName}`
        ).notification((notification) =>
          this.handleNotification(notification)
        );
      }
    },
    unsubscribeFromChannels() {
      if (this.echoChannel) {
        window.Echo.leave(`contract.${this.activeContractName}`);
      }
    },
    handleResize() {
      this.isMobile = window.innerWidth < 992;
      if (this.isMobile) {
        this.isSidebarCollapsed = false;
      }
    }
  }
};
</script>

<style scoped lang="scss">
.app-layout {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.app-layout__cabinet {
  flex: 1;
}

.app-layout__main {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}

.app-layout__content {
  flex: 1;
  min-height: 0;
}

.app-layout__footer {
  flex-shrink: 0;
}

@media (max-width: 992px) {
  .app-layout__main {
    min-height: auto;
  }
}
</style>
